import { useEffect, useState } from "react";
import s from "../partnerPortal.module.css";
import { Panel, SectionTitle } from "./shared";
import { copyText } from "../portalData";
import {
  partnerGetKeys,
  partnerRotateKey,
  partnerRevokeKey,
  partnerCreateApiKey,
} from "../../../services/partnerCalls";
import { errorHandler } from "../../../utils";
import { DEFAULT_ERROR_MESSAGE } from "../../../constants";
import { Spinner } from "../../../components/UI/Spinner/Spinner";

const prefixFor = (env) => (env === "Live" ? "sk_live_" : "sk_test_");

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [copied, setCopied] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleCopy = (id, value) => {
    copyText(value);
    setCopied(id);
    setTimeout(() => setCopied(""), 1600);
  };

  const fetchAPIKeys = async () => {
    setLoading(true);
    try {
      const res = await partnerGetKeys();
      if (res?.data?.data) {
        setKeys(res.data.data);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setLoading(false);
    }
  };

  // Rotate the secret for one environment. The old key is disabled
  // immediately; the new secret is returned once, inline.
  const doRotate = async (env) => {
    setConfirm(null);
    setBusy(true);
    try {
      const res = await partnerRotateKey(env.toLowerCase());
      const secret = res?.secret;
      setCopied("");
      if (secret) {
        setReveal({
          secrets: [{ env, secret }],
          title: "New secret key",
          sub: "Rotated. Copy the new secret now — you won't see it again.",
        });
      }
      await fetchAPIKeys();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  const doRevoke = async (env) => {
    setConfirm(null);
    setBusy(true);
    try {
      await partnerRevokeKey(env.toLowerCase());
      await fetchAPIKeys();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  // First-time provisioning: issues both a live and a sandbox key pair
  // together and shows both secrets once.
  const doCreate = async () => {
    setBusy(true);
    try {
      const res = await partnerCreateApiKey();
      const data = res?.data?.data;
      setCopied("");
      const secrets = [];
      if (data?.live?.secretKey) secrets.push({ env: "Live", secret: data.live.secretKey });
      if (data?.sandbox?.secretKey) secrets.push({ env: "Sandbox", secret: data.sandbox.secretKey });
      if (secrets.length) {
        setReveal({
          secrets,
          title: "Your API keys are ready",
          sub: "Copy these secrets now — they won't be shown again.",
        });
      }
      await fetchAPIKeys();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  const closeReveal = () => {
    setReveal(null);
    setCopied("");
  };

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  return (
    <div>
      <SectionTitle
        title="API Keys"
        sub="Separate keys for sandbox and live. Reveal a secret once — rotate if lost."
      />

      {loading && <Spinner className="my-10" />}

      {keys?.map((key) => {
        const active = key?.status === "active";
        const envLabelClass = !active
          ? s.keyEnvLabelRevoked
          : key?.env === "Live"
            ? s.keyEnvLabelLive
            : s.keyEnvLabelSandbox;

        return (
          <Panel key={key?.env} className={`${s.panel} ${s.keyPanel}`}>
            <div className={s.keyHeaderRow}>
              <span className={`${s.keyEnvLabel} ${envLabelClass}`}>
                {key?.env} keys{active ? "" : " · revoked"}
              </span>
              <span className={s.keyCreatedLabel}>created {key?.created}</span>
            </div>

            {active ? (
              [
                ["Publishable key", key?.pub, false],
                [
                  "Secret key",
                  `${prefixFor(key?.env)}••••••••••${key?.last4}`,
                  true,
                ],
              ].map(([label, value, isSecret]) => {
                const copyId = `${key?.env}:${label}`;
                return (
                  <div key={label} className={s.keyRow}>
                    <div className={s.keyRowInfo}>
                      <div className={s.keyRowLabel}>{label}</div>
                      <div className={s.keyValue}>{value}</div>
                    </div>
                    <div className={s.keyRowActions}>
                      {!isSecret && (
                        <button
                          type="button"
                          className={s.btnGhost}
                          onClick={() => handleCopy(copyId, key?.pub)}
                        >
                          {copied === copyId ? "Copied" : "Copy"}
                        </button>
                      )}
                      {isSecret && (
                        <>
                          <button
                            type="button"
                            className={s.btnGhost}
                            disabled={busy}
                            onClick={() =>
                              setConfirm({ env: key?.env, action: "rotate" })
                            }
                          >
                            Rotate
                          </button>
                          <button
                            type="button"
                            className={`${s.btnGhost} ${s.btnGhostDanger}`}
                            disabled={busy}
                            onClick={() =>
                              setConfirm({ env: key?.env, action: "revoke" })
                            }
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={s.revokedBanner}>
                <div className={s.revokedTitle}>
                  Key revoked — API access is off
                </div>
                <div className={s.revokedText}>
                  Your {key?.env?.toLowerCase()} key is disabled. To restore
                  access, contact AwaSource to provision a new key — you'll get
                  a one-time link to reveal the new secret.
                </div>
              </div>
            )}
          </Panel>
        );
      })}

      {keys?.length > 0 && (
        <>
          <div className={s.note}>
            Rate limit: 1,000 requests / minute per key. Secret keys are shown
            only at creation.
          </div>
          <div className={`${s.note} ${s.noteTop}`}>
            Received a key-setup link from AwaSource? Open that emailed link to
            reveal your secret — it's shown once and can't be displayed again
            here. Lost it? Rotate above to issue a new secret.
          </div>
        </>
      )}

      {!loading && keys?.length === 0 && (
        <div className="flex flex-col items-center my-20 mx-auto gap-4">
          <div className="text-sm text-[#4a5a7a]">
            You don't have any API keys yet.
          </div>
          <button
            type="button"
            className={s.btnSolid}
            disabled={busy}
            onClick={doCreate}
          >
            {busy ? "Creating…" : "Create API key"}
          </button>
        </div>
      )}

      {confirm && (
        <div className={s.modalOverlay} onClick={() => setConfirm(null)}>
          <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalBody}>
              <div className={s.modalTitle}>
                {confirm.action === "rotate"
                  ? `Rotate ${confirm?.env?.toLowerCase()} secret key`
                  : `Revoke ${confirm?.env?.toLowerCase()} key`}
              </div>
              <div className={s.modalDesc}>
                {confirm.action === "rotate"
                  ? "This issues a new secret and immediately disables the current one. Anything still using the old secret will start failing until you deploy the new one. You'll see the new secret once."
                  : "This disables your key with no replacement. Every request starts failing immediately, and you'll need AwaSource to provision a new key to restore access."}
              </div>
            </div>
            <div className={s.modalActions}>
              <button
                type="button"
                className={s.modalBtnCancel}
                onClick={() => setConfirm(null)}
              >
                Cancel
              </button>
              {confirm.action === "rotate" ? (
                <button
                  type="button"
                  className={s.modalBtnPrimary}
                  disabled={busy}
                  onClick={() => doRotate(confirm?.env)}
                >
                  {busy ? "Rotating…" : "Rotate key"}
                </button>
              ) : (
                <button
                  type="button"
                  className={s.modalBtnDanger}
                  disabled={busy}
                  onClick={() => doRevoke(confirm?.env)}
                >
                  {busy ? "Revoking…" : "Revoke key"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {reveal && (
        <div className={s.modalOverlay} onClick={closeReveal}>
          <div
            className={`${s.modalCard} ${s.modalCardWide}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.revealHeader}>
              <div className={s.revealIcon}>🔑</div>
              <div className={s.revealTitle}>{reveal.title}</div>
              <div className={s.revealSub}>{reveal.sub}</div>
            </div>
            <div className={s.revealBody}>
              {(reveal.secrets || []).map(({ env, secret }) => {
                const copyId = `reveal:${env}`;
                return (
                  <div key={env}>
                    <div className={s.secretLabel}>{env} secret key</div>
                    <div className={s.secretRow}>
                      <input
                        readOnly
                        value={secret}
                        onFocus={(e) => e.target.select()}
                        className={`${s.secretInput} ${copied === copyId ? s.secretInputCopied : ""}`}
                      />
                      <button
                        type="button"
                        className={`${s.secretCopyBtn} ${
                          copied === copyId ? s.secretCopyBtnCopied : ""
                        }`}
                        onClick={() => handleCopy(copyId, secret)}
                      >
                        {copied === copyId ? "Copied ✓" : "Copy"}
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className={s.revealWarning}>
                <div className={s.revealWarningText}>
                  This is the only time this secret is shown. Store it in your
                  secret manager now — if you lose it, you'll have to rotate
                  again.
                </div>
              </div>
              <button
                type="button"
                className={s.revealCloseBtn}
                onClick={closeReveal}
              >
                I've saved it — close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
